import { Fragment } from "react"

/** The panel's horizontal padding (`px-2.5`), in px. Shared with the total
 * row, which cancels it to reach the borders. */
const PANEL_INLINE_PADDING = 10

/** Added on each side of the overlay rule so its clear space matches the
 * total's. The field list is `gap-1` (4px) and the total's rule sits in 6px,
 * so the rule needs the 2px difference on both sides. */
const ROW_GAP_TOP_UP = 2

type ChartTooltipProps = {
  header?: string
  footerTimestamp?: string
  fields: string[] // Pre-formatted field HTML from Highcharts
  /** How many of the leading rows are overlay series — readings on their own
   * axis, in their own unit, that the stack's total does not include. A rule
   * is drawn under them so they don't read as the largest bands. */
  overlayFieldCount?: number
  showTotal?: boolean
  totalTooltip?: string // Pre-formatted total HTML
}

export function ChartTooltip({
  header,
  footerTimestamp,
  fields,
  overlayFieldCount = 0,
  showTotal,
  totalTooltip,
}: ChartTooltipProps) {
  const limitedFields = fields.slice(0, 10)
  const remainingFieldCount = fields.length - limitedFields.length
  // Only when there is something on both sides of it — a rule under the last
  // row would read as an underline rather than a separator.
  const dividerAfterIndex =
    overlayFieldCount > 0 && overlayFieldCount < limitedFields.length ? overlayFieldCount - 1 : -1

  return (
    <div className="py-2 px-2.5 text-xs z-[75] flex flex-col gap-1.5 rounded-md shadow-lg bg-bg-surface dark:bg-neutral-900 border border-solid border-border-default">
      {header && <p className="text-xs text-fg-secondary">{header}</p>}

      {footerTimestamp && <p className="text-xs text-fg-secondary">{footerTimestamp}</p>}

      <div className="flex flex-col gap-1">
        {limitedFields.map((field, i) =>
          field ? (
            <Fragment key={i}>
              <div
                className="flex items-center flex-nowrap gap-6 justify-between"
                // biome-ignore lint/security/noDangerouslySetInnerHtml: Chart tooltip HTML is internally generated from Highcharts
                dangerouslySetInnerHTML={{ __html: field }}
              />
              {i === dividerAfterIndex && (
                // Full-bleed like the total's rule below, by the same
                // inline-pixel route — see the note on that one for why these
                // cannot be Tailwind utilities. The block margins top up this
                // list's 4px gap to the 6px of clear space the total's rule
                // gets on each side, so the two rules share one rhythm.
                <div
                  style={{
                    marginInline: -PANEL_INLINE_PADDING,
                    marginBlock: ROW_GAP_TOP_UP,
                  }}
                  className="border-t border-solid border-border-default"
                />
              )}
            </Fragment>
          ) : null,
        )}
      </div>
      {remainingFieldCount > 0 && (
        <div className="text-xs text-fg-secondary">Click to view +{remainingFieldCount} more</div>
      )}

      {showTotal && fields.filter(Boolean).length > 1 && totalTooltip && (
        <div
          // `font-semibold` on the wrapper rather than the inner spans: the
          // row's markup comes from Highcharts' own body formatter, so weight
          // has to be inherited into it. Two steps up from the rows rather than
          // one — at 12px a single step is about a pixel of extra width, which
          // is not enough to find the row at a glance.
          // Pulled out to the panel edges and padded back in, so the rule meets
          // both borders. A divider that stops short reads as an underline for
          // the last band rather than a separator above the total.
          //
          // Inline rather than `-mx-2.5 px-2.5`: the frontend's Tailwind scans
          // only its own `src`, so a utility that appears nowhere but this
          // package compiles to nothing and fails silently. 10px is this
          // panel's own `px-2.5` — the two have to stay equal and opposite.
          style={{ marginInline: -PANEL_INLINE_PADDING, paddingInline: PANEL_INLINE_PADDING }}
          className="flex items-center justify-between border-t border-solid border-border-default pt-1.5 font-semibold"
          // biome-ignore lint/security/noDangerouslySetInnerHtml: Chart tooltip HTML is internally generated from Highcharts
          dangerouslySetInnerHTML={{ __html: totalTooltip }}
        />
      )}
    </div>
  )
}
